/*
 * Authors: mariushirjanu
 * Created: 25/09/2017
 * Upgraded: 06/09/2022
 */
import { Input, Component, OnInit } from '@angular/core';
import { FormattingService } from '../../../shared/services/formatting.service';

import { ICountry, ICountryExtended, ICurrency, ILocationExtended, ISubCompany } from '../../../../type_definitions/datatypes';
import { LOV_CURRENCY, LOV_STATE, LovService } from '../../../shared/services/lov.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { DefaultFormDialogComponent } from '../../../shared/message/dialogs/default-form-dialog.component';
import { TranslationService } from '../../../shared/translation/translation.service';

@Component({
  selector: 'location-edit',
  templateUrl: '../../../shared/message/dialogs/default-form-dialog.html'
})
export class LocationEditComponent extends DefaultFormDialogComponent implements OnInit {
  @Input() public model:ILocationExtended;
  @Input() public customerSubCompanyMandatory:boolean;
  @Input() public subCompaniesPromise:Promise<ISubCompany[]>;
  @Input() public allCountriesPromise:Promise<ICountry[]>;
  @Input() public customerCurrencies:ICurrency[];
  private stateField:IExtraFieldConfigurationObject;

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private lovService:LovService,
    protected validateService:GaValidateService,
    protected translationService:TranslationService
  ) {
    super(validateService, translationService);
  }

  public ngOnInit():void {
    super.ngOnInit();
    this.fields = this.createFields();
    this.showHideStateField(<ICountryExtended>this.model.country);
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const locationNameField:IExtraFieldConfigurationObject = this.formlyService.anInput('name', 'GA.LOCATION.EDIT.LOCATION_NAME')
      .isRequired()
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withPlaceholder()
      .build();

    const addressField:IExtraFieldConfigurationObject = this.formlyService.anInput('address', 'GA.GENERAL.ADDRESS')
      .isRequired()
      .withoutWrapper()
      .withWrapperClass('col-12 col-space-bottom')
      .withPlaceholder()
      .build();
    const zipField:IExtraFieldConfigurationObject = this.formlyService.anInput('zipCode', 'GA.GENERAL.ZIP')
      .isRequired()
      .withoutWrapper()
      .withWrapperClass('col-4 pe-1')
      .withPlaceholder()
      .build();
    const cityField:IExtraFieldConfigurationObject = this.formlyService.anInput('city', 'GA.GENERAL.CITY')
      .isRequired()
      .withoutWrapper()
      .withWrapperClass('col-8')
      .withPlaceholder()
      .build();
    const zipCityGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup()
      .withFields([
        zipField,
        cityField
      ])
      .withoutWrapper()
      .withWrapperClass('col-12 col-space-bottom')
      .withExtraOptions({
        templateOptions: {
          fieldGroupWrapperClass: 'g-0'
        }
      })
      .withPlaceholder()
      .build();
    this.stateField = this.formlyService.aSelect('state', 'GA.GENERAL.STATE')
      .isRequired()
      .withFormat(this.formattingService.formatState)
      .withoutWrapper()
      .withWrapperClass('col-12 col-space-bottom')
      .build();

    let countryField:IExtraFieldConfigurationObject;
    if (this.allCountriesPromise) {
      countryField = this.formlyService.aSelect('country', 'GA.GENERAL.COUNTRY')
        .isRequired()
        .withFormat(this.formattingService.formatCountry)
        .withoutWrapper()
        .withWrapperClass('col-12 col-space-bottom group')
        .isDisabled()
        .onChange(() => {
          this.showHideStateField(<ICountryExtended>this.model.country);
        })
        .build();
      this.loadCountries(countryField);
    } else {
      countryField = this.formlyService.aValue('country', 'GA.GENERAL.COUNTRY')
        .isRequired()
        .withFormat(this.formattingService.formatCountry)
        .withoutWrapper()
        .withWrapperClass('col-12 col-space-bottom group')
        .build();
    }

    const addressGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup('addressGroup', 'GA.GENERAL.ADDRESS')
      .withFields([
        addressField,
        zipCityGroup,
        this.stateField,
        countryField
      ])
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-12')
      .build();

    const fields:IExtraFieldConfigurationObject[] = [];
    fields.push(locationNameField, addressGroup);

    const homepageField:IExtraFieldConfigurationObject = this.formlyService.anInput('homePage', 'GA.GENERAL.HOMEPAGE')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withPlaceholder()
      .build();
    const phoneField:IExtraFieldConfigurationObject = this.formlyService.anInput('telephoneNumber', 'GA.GENERAL.PHONE')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .withPlaceholder()
      .build();
    const faxField:IExtraFieldConfigurationObject = this.formlyService.anInput('faxNumber', 'GA.GENERAL.FAX')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .withPlaceholder()
      .build();
    const phoneFaxGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup(undefined)
      .withFields([
        phoneField,
        faxField
      ])
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .build();
    const companyNameField:IExtraFieldConfigurationObject = this.formlyService.anInput('companyRegisteredName', 'GA.LOCATION.EDIT.COMPANY_REGISTERED_NAME')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withPlaceholder()
      .build();
    const vatnrField:IExtraFieldConfigurationObject = this.formlyService.anInput('vatNumber', 'GA.GENERAL.VATTAX')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .withPlaceholder()
      .build();
    const companyRegistrationNrField:IExtraFieldConfigurationObject = this.formlyService.anInput('companyRegistrationNumber', 'GA.LOCATION.EDIT.COMPANY_REGISTRATION_NR')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .withPlaceholder()
      .build();
    const vatRegistrationGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup(undefined)
      .withFields([
        vatnrField,
        companyRegistrationNrField
      ])
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .build();
    const dateIncorporationField:IExtraFieldConfigurationObject = this.formlyService.aDateInput('incorporationDate', 'GA.LOCATION.EDIT.DATE_OF_INCORPORATION')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .withPlaceholder()
      .build();
    const creditLimitField:IExtraFieldConfigurationObject = this.formlyService.anInput('creditLimit', 'GA.LOCATION.EDIT.CREDIT_LIMIT')
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-right')
      .withPlaceholder()
      .build();
    const currencyField:IExtraFieldConfigurationObject = this.formlyService.aSelect('currencyCreditLimit', 'GA.GENERAL.CURRENCY')
      .withFormat(this.formattingService.formatCurrencyS)
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-left')
      .withOptions(this.transformCurrencies(this.customerCurrencies))
      .build();
    if (!this.customerCurrencies) {
      this.loadAllCurrencies(currencyField);
    }
    const creditLimitGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup('creditLimitGroup', 'GA.LOCATION.EDIT.CREDIT_LIMIT')
      .withFields([
        creditLimitField,
        currencyField
      ])
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-6')
      .build();
    const dateCreditLimitGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup(undefined)
      .withFields([
        dateIncorporationField,
        creditLimitGroup
      ])
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .build();

    let subcompanyField:IExtraFieldConfigurationObject;
    if (this.subCompaniesPromise) {
      subcompanyField = this.formlyService.aSelect('subCompany', 'GA.GENERAL.SUBCOMPANY')
        .withFormat(this.formattingService.formatValue.bind(null, 'subcompany'))
        .withLabelClass('col-md-12')
        .withValueClass('col-md-12')
        .withPlaceholder()
        .isDisabled()
        .build();

      subcompanyField.templateOptions.required = this.customerSubCompanyMandatory;

      fields.push(subcompanyField);
      this.loadSubCompanies(subcompanyField);
    }

    fields.push(
      homepageField,
      phoneFaxGroup,
      companyNameField,
      vatRegistrationGroup,
      dateCreditLimitGroup
    );

    let activeField:IExtraFieldConfigurationObject;
    if (!this.adding) {
      activeField = this.formlyService.aCheckbox('active', 'GA.GENERAL.ACTIVE')
        .build();
      fields.push(activeField);
    }

    return fields;
  }

  private loadSubCompanies(subCompanyField:IExtraFieldConfigurationObject):void {
    this.subCompaniesPromise
      .then((subCompanies:ISubCompany[]) => {
        subCompanyField.templateOptions.options = subCompanies;
      })
      .finally(() => {
        this.setLoading(subCompanyField, false);
      });
  }

  private loadCountries(countryField:IExtraFieldConfigurationObject):void {
    this.allCountriesPromise
      .then((countries:ICountry[]) => {
        countryField.templateOptions.options = countries;
      })
      .finally(() => {
        this.setLoading(countryField, false);
      });
  }

  private loadAllCurrencies(currencyField:IExtraFieldConfigurationObject):void {
    this.lovService.list(LOV_CURRENCY)
      .then((currencies:ICurrency[]) => {
        currencyField.templateOptions.options = this.transformCurrencies(currencies);
      })
      .finally(() => {
        this.setLoading(currencyField, false);
      });
  }

  private setLoading(field:IExtraFieldConfigurationObject, loading:boolean):void {
    field.templateOptions.loading = loading;
    field.templateOptions.disabled = loading;
  }

  private transformCurrencies(currencies:ICurrency[]):ICurrency[] {
    if (currencies) {
      return currencies.map((currency:ICurrency) => {
        return {
          id: currency.isoCode,
          value: currency.description
        };
      });
    }
  }

  private showHideStateField(country:ICountryExtended):void {
    if (country?.hasState) {
      this.stateField.hide = false;
      this.formlyService.fillField(this.stateField, this.lovService.list(`${LOV_STATE}-${country.id}`));
    } else {
      this.stateField.templateOptions.options = [];
      this.stateField.hide = true;
    }
  }
}